import type { ComponentType } from "react";
import {
  authenticatedRoutes as rawAuthenticatedRoutes,
  unauthenticatedRoutes as rawUnauthenticatedRoutes,
} from "../helpers/routeDefinitions";

export type RouteAccess = "authenticated" | "public";
type PermissionAction = string | readonly string[];

export interface RoutePermission {
  action: PermissionAction;
  resource: string;
}

interface RouteLocation {
  hash: string;
  pathname: string;
  search: string;
  state?: unknown;
}

export interface OwnedRouteProps {
  location: RouteLocation;
  match: {
    params: Record<string, string | undefined>;
  };
}

export interface AppRoute {
  access: RouteAccess;
  component: ComponentType<OwnedRouteProps>;
  exact: boolean;
  id: string;
  path?: string;
  permission?: RoutePermission;
}

interface RawRoute {
  component: ComponentType<OwnedRouteProps>;
  exact?: boolean;
  path?: string;
  permission?: RoutePermission;
}

function routeId(access: RouteAccess, path: string | undefined): string {
  return path ? `${access}:${path}` : `${access}:not-found`;
}

function normalizeRoute(route: RawRoute, access: RouteAccess): AppRoute {
  return Object.freeze({
    access,
    component: route.component,
    exact: route.exact ?? false,
    id: routeId(access, route.path),
    path: route.path,
    permission: route.permission,
  });
}

function validateManifest(routes: readonly AppRoute[]): void {
  const ids = new Set<string>();
  const paths = new Set<string>();
  let catchAllCount = 0;

  for (const route of routes) {
    if (ids.has(route.id)) throw new Error(`Duplicate route id: ${route.id}`);
    ids.add(route.id);

    if (!route.path) {
      catchAllCount += 1;
    } else {
      if (paths.has(route.path)) throw new Error(`Duplicate route path: ${route.path}`);
      paths.add(route.path);
    }

    if (route.permission) {
      if (route.access !== "authenticated") {
        throw new Error(`Public route ${route.id} cannot require a permission`);
      }
      if (!route.permission.resource || !route.permission.action) {
        throw new Error(`Route ${route.id} has invalid permission metadata`);
      }
    }
  }

  if (catchAllCount !== 1 || routes.at(-1)?.path) {
    throw new Error("The route manifest must end with exactly one catch-all route");
  }
}

export const unauthenticatedRoutes = Object.freeze(
  (rawUnauthenticatedRoutes as RawRoute[]).map((route) => normalizeRoute(route, "public")),
);

export const authenticatedRoutes = Object.freeze(
  (rawAuthenticatedRoutes as RawRoute[]).map((route) => normalizeRoute(route, "authenticated")),
);

export const routeManifest = Object.freeze([...unauthenticatedRoutes, ...authenticatedRoutes]);

validateManifest(routeManifest);
